import { useCallback, useState } from 'react';
import DropdownSelect from '../../components/DropdownSelect';
import { Box, CircularProgress } from '@mui/material';
import {
  ButtonContainer,
  RoundedButton,
  SchoolStaffSearchHeaderWrapper,
  SearchInputWrapper
} from './styled';
import users from '../../apis/users';
import { StaffProps } from './types';
import { transformFetchStaffUsers } from './utils/transformApiResponse';
import AddNewStaffForm from './components/AddNewStaffForm';
import ToastMessage from '../../components/ToastMessage';
import { SnackbarSeverityType } from '../../enums';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux/store';
import StaffDetailPage from './StaffDetailPage';
import TextInputWithIcon from '../../components/TextInputWithIcon';
import { getStaffProfileRoles } from './config/constant';
import StaffTable from './components/StaffTable';
import Icons from '../../components/icons';
import { PageWrapper, Title } from '../common/styled';

const StaffProfilePage = () => {
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [staffName, setStaffName] = useState<string>('');

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSearched, setIsSearched] = useState(false);
  const [staffData, setStaffData] = useState<StaffProps[]>([]);
  const [isAddNewStaffModalOpen, setIsAddNewStaffModalOpen] = useState(false);

  const [isDetailPage, setIsDetailPage] = useState(false);
  const [selectedStaffId, setSelectedStaffId] = useState('');

  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastSeverity, setToastSeverity] = useState<SnackbarSeverityType>(
    SnackbarSeverityType.success
  );

  const { user: loggedInUser } = useSelector(
    (state: RootState) => state.profile
  );

  const handleCloseToast = () => {
    setToastOpen(false);
  };

  const showToast = useCallback(
    (message: string, severity: SnackbarSeverityType) => {
      setToastMessage(message);
      setToastSeverity(severity);
      setToastOpen(true);
    },
    []
  );

  const fetchSchoolStaffUsers = async () => {
    try {
      const params = {
        role: selectedRole,
        staffName,
        school: loggedInUser.school
      };
      const response = await users.fetchUsersByRoleAndName(params);
      const transformedStaffData = transformFetchStaffUsers(response as any);
      setStaffData(transformedStaffData);
    } catch (error: any) {
      showToast(
        error.errors?.[0].message || 'Failed to fetch staff',
        SnackbarSeverityType.error
      );
    }
  };

  const handleStaffSearch = async () => {
    setIsSubmitting(true);
    await fetchSchoolStaffUsers();
    setIsSubmitting(false);
    setIsSearched(true);
  };

  const handleAddNewStaffModel = () => {
    setIsAddNewStaffModalOpen(!isAddNewStaffModalOpen);
  };

  const handleEditClick = (id?: string) => {
    if (id) setSelectedStaffId(id);
    setIsDetailPage(!isDetailPage);
  };

  return (
    <Box>
      {!isDetailPage && (
        <Box>
          <SchoolStaffSearchHeaderWrapper>
            <Title>School Staff {isDetailPage ? 'Detail' : 'Search'}</Title>
            <RoundedButton
              onClick={handleAddNewStaffModel}
              disabled={isSubmitting}
            >
              {isSubmitting ? (
                <CircularProgress size={24} color='inherit' />
              ) : (
                <>
                  <Icons.PlusWhite style={{ marginRight: '6px' }} /> Add New
                  Staff User
                </>
              )}
            </RoundedButton>
          </SchoolStaffSearchHeaderWrapper>
          <PageWrapper sx={{ minHeight: 'auto' }}>
            <SearchInputWrapper>
              <DropdownSelect
                label='Role'
                name='role'
                value={selectedRole}
                options={getStaffProfileRoles(loggedInUser?.school)}
                placeholder='Select Role'
                onChange={(event) => {
                  setSelectedRole(event.target.value as string);
                  setIsSearched(false);
                }}
                hasTopLabel
                required
              />

              <TextInputWithIcon
                label='Staff Name'
                required={false}
                value={staffName}
                onChange={(e) => {
                  setStaffName(e.target.value);
                  setIsSearched(false);
                }}
                placeholder='Search by Staff Name'
              />
              <ButtonContainer>
                <RoundedButton
                  onClick={handleStaffSearch}
                  disabled={isSubmitting || !selectedRole}
                  sx={{
                    marginTop: '24px'
                  }}
                >
                  <Icons.SearchWhiteOutlined style={{ marginRight: '6px' }} />
                  {isSubmitting ? (
                    <CircularProgress size={24} color='inherit' />
                  ) : (
                    'Search'
                  )}
                </RoundedButton>
              </ButtonContainer>
            </SearchInputWrapper>
          </PageWrapper>
          {isSearched && (
            <StaffTable
              handleEditClick={handleEditClick}
              isLoading={isSubmitting}
              staffData={staffData}
            />
          )}
        </Box>
      )}
      <AddNewStaffForm
        open={isAddNewStaffModalOpen}
        onClose={handleAddNewStaffModel}
        showToast={showToast}
      />

      {isDetailPage && (
        <StaffDetailPage
          showToast={showToast}
          selectedTeacherId={selectedStaffId}
          handleRowEdit={handleEditClick}
        />
      )}
      <ToastMessage
        open={toastOpen}
        message={toastMessage}
        severity={toastSeverity}
        onClose={handleCloseToast}
      />
    </Box>
  );
};

export default StaffProfilePage;
